import { EditAnalysisFeeCalculatorReturnType } from "@hooks/useFeeCalculator";
import { UserType } from "@models/auth";
import { BooleanKeys } from "@models/core";
import { CompareOperator } from "@models/core/compareOperator";
import {
  MAAnalysisTooltipDTO,
  ManagedAccountFeeAnalysisContainerDTO,
} from "@models/managed-accounts/entities/step/fee";
import { SelectedInvestmentDTO } from "@models/managed-accounts/entities/step/setup";
import { BalanceAvailability } from "@models/platform-analysis/enums/fee/balanceAvailability";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeCalculatorSectionId } from "@models/platform-analysis/enums/fee/feeCalculatorSectionId";
import { AnalysisCalculateTotalTypeVariable } from "@models/platform-analysis/enums/fee/feeCalculatorTotalType";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import { FeeCalculatorRenderFieldType } from "@models/platform-analysis/enums/fee/renderFieldType";
import { generateUUID } from "@utils";
import { TemplateExecutor } from "lodash";
import { FeeCalculatorFieldType } from "../../enums/fee/fieldType";
import { AnalysisVariable } from "../../enums/variableName";
import { AnalysisSelectionDTO } from "../analysis";
import { FeePortfolioDetailDTO } from "../portfolioDetails";
import { ImageDTO, SelectedProductDTO } from "./feature";
import { SetupStepRenderSectionConfig } from "./setup";
import { PortfolioCompositionDTO, ProductRecommendationDTO } from "./summary";

//TODO: Make Platform Analysis models to consistent with BE
//Fee calculator
export class FamilyMemberAccountDTO {
  id: string | null = generateUUID();
  balance = 0;
  variableName?: AnalysisVariable | null = AnalysisVariable.IDPS_1;
  isIDPS = false;
  order = 0;
  accountIndex?: number = 0;
}

export class FamilyMemberDTO {
  id: string | null = generateUUID();
  name: string | null = null;
  variableName?: AnalysisVariable | null = AnalysisVariable.IDPS_1;
  order = 0;
  totalBalance = 0;
  familyMemberAccounts: FamilyMemberAccountDTO[] = [];
}

export class FeeCalculatorDataDTO {
  id?: string | null = null;
  value: string | null = null;
  weight?: number | null = null;
}

export class FeeCalculatorDTO {
  id: string | null = generateUUID();
  name = "";
  secondaryName?: string | null = null;
  description: string | null = null;
  fieldType: FeeCalculatorFieldType = FeeCalculatorFieldType.Numeric;
  variableName: AnalysisVariable = AnalysisVariable.Choose_AU;
  defaultValue: string | null = null;
  feeCalculatorData: FeeCalculatorDataDTO | null = null;
  isMissingInvestment?: boolean = false;
  showInPortfolio?: boolean = false;
  balanceAvailability?: BalanceAvailability = BalanceAvailability.Show;
  hasUnavailableInvestmentOption?: boolean = false;
}

export class AnalysisFeeCalculatorDTO extends AnalysisSelectionDTO {
  familyMembers: FamilyMemberDTO[] = [];
  feeCalculators: FeeCalculatorDTO[] = [];
  isShowHoldingsNumber?: boolean = false;
  containsExpand?: boolean = false;
  containsCfsEdge?: boolean = false;
  containsNorth?: boolean = false;
  containsCps?: boolean = false;
  portfolioSetups?: AnalysisFeePortfolioSetupDTO[] = [];
  portfolios?: AnalysisFeePortfolioDTO[] = [];
}

// Portfolio setup
export class AnalysisFeePortfolioSetupDTO {
  id = "";
  name = "";
  subProductId?: string;
  subProductName?: string;
  product: AnalysisFeePortfolioSetupProductDTO | null = null;
  selectedPortfolioId?: string | null = null;
  excludedFromFeeAnalysis?: boolean = false;
  hasNoInvestmentData?: boolean = false;
  hasAllocationError?: boolean = false;
  isMissingInvestment?: boolean = false;
  isMissingInvestmentOption?: boolean = false;
  isBelowMinimumInvestmentSize?: boolean = false;
  setupType: AnalysisFeePortfolioSetupType =
    AnalysisFeePortfolioSetupType.SubProduct;
  missingInvestmentTooltip?: MAAnalysisTooltipDTO | null = null;
  missingInvestmentOptionTooltip?: MAAnalysisTooltipDTO | null = null;
  belowMinimumInvestmentSizeTooltip?: MAAnalysisTooltipDTO | null = null;
  rateCardVersion?: number | null = null;
  termDepositsAvailableInIDPS?: boolean = false;
  termDepositsAvailableInSuper?: boolean = false;
  unlistedManagedFundsAvailableInIDPS?: boolean = false;
  unlistedManagedFundsAvailableInSuper?: boolean = false;
  auListedSecuritiesAvailableInIDPS?: boolean = false;
  auListedSecuritiesAvailableInSuper?: boolean = false;
  intlListedSecuritiesAvailableInIDPS?: boolean = false;
  intlListedSecuritiesAvailableInSuper?: boolean = false;
  fixedIncomeAvailableInIDPS?: boolean = false;
  fixedIncomeAvailableInSuper?: boolean = false;
  cashAvailableInIDPS?: boolean = true; // TODO: Hardcode for Praemium Spectrum, need refactor
  cashAvailableInSuper?: boolean = true; // TODO: Hardcode for Praemium Spectrum, need refactor
  outdatedAt?: Date | null = null;
  isLatest?: boolean = false;
  hasUnavailableInvestmentOption?: boolean = false;
  unavailableInvestmentOptionTooltip?: MAAnalysisTooltipDTO | null = null;
  hasUnavailableInvestment?: boolean = false;
  unavailableInvestmentTooltip?: MAAnalysisTooltipDTO | null = null;
  hasCashNotAvailableInIDPSError?: boolean = false; // TODO: Hardcode for Praemium Spectrum, need refactor
  hasCashNotAvailableInSuperError?: boolean = false; // TODO: Hardcode for Praemium Spectrum, need refactor
}

export class AnalysisFeePortfolioSetupProductDTO {
  id = "";
  name = "";
  logo?: string | null = null;
  isProductMain?: boolean = false;
}

export class AnalysisFeePortfolioDTO {
  id = "new";
  name = "";
  order?: number = 0;
  selectedTemplateId?: string | null = null;
  selectedInvestments: SelectedInvestmentDTO[] = [];
  feeCalculatorData: PortfolioFeeCalculatorDataDTO[] = [];
  favouritedInvestmentShCode?: string[] = [];
  unFavouritedInvestmentShCode?: string[] = [];
  belowMinimumInvestmentSizeTooltip?: MAAnalysisTooltipDTO | null = null;
  missingInvestmentOptionTooltipData?: MAAnalysisTooltipDTO | null = null;
  missingInvestmentTooltip?: MAAnalysisTooltipDTO | null = null;
}

export class PortfolioFeeCalculatorDataDTO {
  variableName: AnalysisVariable = AnalysisVariable.Choose_AU;
  value: string | null = null;
  weight?: number | null = null;
  isMissingInvestment?: boolean = false;
  balanceAvailability?: BalanceAvailability = BalanceAvailability.Show;
  hasUnavailableInvestmentOption?: boolean = false;
}

export class PortfolioAssignmentItemDTO {
  subProductId = "";
  rateCardId?: string | null = "";
  rateCardVersion?: number | null = null;
  portfolioId?: string = "";
}

export class PortfolioAssignmentValidationRequestDTO {
  portfolioId = "";
  totalBalance = 0;
  items: PortfolioAssignmentItemDTO[] = [];
  hasIDPSBalance?: boolean = false;
  hasSuperBalance?: boolean = false;
  shouldSaveWarning?: boolean = false;
}

export class PortfolioAssignmentValidationResultDTO {
  id = "";
  subProductId?: string = "";
  setupType: AnalysisFeePortfolioSetupType =
    AnalysisFeePortfolioSetupType.SubProduct;
  rateCardVersion?: number = 0;
  isMissingInvestment = false;
  isBelowMinimumInvestmentSize = false;
  isMissingInvestmentOption = false;
  missingInvestmentTooltip: MAAnalysisTooltipDTO | null = null;
  belowMinimumInvestmentSizeTooltip: MAAnalysisTooltipDTO | null = null;
  missingInvestmentOptionTooltip: MAAnalysisTooltipDTO | null = null;
  hasAllocationError?: boolean = false;
  hasUnavailableInvestmentOption?: boolean = false;
  unavailableInvestmentOptionTooltip?: MAAnalysisTooltipDTO | null = null;
  hasUnavailableInvestment?: boolean = false;
  unavailableInvestmentTooltip?: MAAnalysisTooltipDTO | null = null;
}

//Fee analysis
export interface AnalysisFeeDTO {
  /** @deprecated Property removed on merged PA/SR */
  selectedProducts: SelectedProductDTO[];
  /** @deprecated Property removed on merged PA/SR */
  subProducts: FeeSubProductDTO[];
  /** @deprecated Property removed on merged PA/SR */
  isShowHoldingsNumber: boolean;

  all: AnalysisFeeContainerDTO;
  shortlisted?: AnalysisFeeContainerDTO;
  disqualified?: AnalysisFeeContainerDTO;
}

export interface AnalysisFeeContainerDTO {
  selectedProducts: SelectedProductDTO[];
  subProducts: FeeSubProductDTO[];
  isShowHoldingsNumber: boolean;
  portfolioSetup: FeeCalculatorPortfolioLevel;
}

export interface ExportableAnalysisStepFeeDTO extends AnalysisFeeDTO {
  name?: string;
  supplierName?: string;
  ownerName?: string;
  adviserName?: string;
  practiceName?: string;
  ownerType?: UserType;
  serial?: string;
  feePortfolioDetails?: FeePortfolioDetailDTO;
  lastModifiedDate?: Date;
  feesDisplayStyle?: FeesDisplayStyle;
  feeMissingInvestmentsStyle?: FeeMissingInvestmentsOption;
  investmentMenu?: FeeInvestmentMenuOption;
  feeTableViewMode?: FeeHeatmapMode;
  smaTableViewMode?: FeesDisplayMode;
  excludePerformanceInSMATable?: boolean;
  /** @deprecated Property removed on merged PA/SR */
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  smaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
}

export interface AnalysisStepFeeDTO {
  name?: string;
  /** @deprecated Property removed on merged PA/SR */
  supplierName?: string;
  ownerName?: string;
  adviserName?: string;
  practiceName?: string;
  firmName?: string;
  familyGroupName?: string;
  ownerType?: UserType;
  serial?: string;
  feePortfolioDetails?: FeePortfolioDetailDTO;
  lastModifiedDate?: Date;
  feesDisplayStyle?: FeesDisplayStyle;
  feeMissingInvestmentsStyle?: FeeMissingInvestmentsOption;
  investmentMenu?: FeeInvestmentMenuOption;
  feeTableViewMode?: FeeHeatmapMode;
  smaTableViewMode?: FeesDisplayMode;
  excludePerformanceInSMATable?: boolean;
  /** @deprecated Property removed on merged PA/SR */
  selectedProducts: SelectedProductDTO[];
  /** @deprecated Property removed on merged PA/SR */
  subProducts: FeeSubProductDTO[];
  /** @deprecated Property removed on merged PA/SR */
  isShowHoldingsNumber: boolean;
  /** @deprecated Property removed on merged PA/SR */
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  smaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
  disqualifiedSmaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
  portfolioCompositionByPlatform?: PortfolioCompositionDTO;
  disqualifiedPortfolioCompositionByPlatform?: PortfolioCompositionDTO;
  all: AnalysisFeeContainerDTO;
  shortlisted?: AnalysisFeeContainerDTO;
  disqualified?: AnalysisFeeContainerDTO;
}

export interface FeeSubProductDTO {
  id: string;
  name: string;
  productId: string;
  productName?: string;
  productLogo?: ImageDTO;
  isProductMain?: boolean;
  order: number;
  warning?: string[];
  fees: FeeDTO[];
  detail: {
    totalFees?: null | number;
    totalFeesExcPerf?: null | number;
    totalFeesPercentage?: null | number;
    totalFeesExcPerfPercentage?: null | number;
    idpsAdminAndAccFees?: null | number;
    idpsAdminAndAccFeesPercentage?: null | number;
    superAdminAndAccFees?: null | number;
    superAdminAndAccFeesPercentage?: null | number;
    transactionFees?: null | number;
    transactionFeesPercentage?: null | number;
    intlSecuritiesOrBondHoldingFees?: null | number;
    intlSecuritiesOrBondHoldingFeesPercentage?: null | number;
    cashFees?: null | number;
    cashFeesPercentage?: null | number;
    expenseRecoveryAndOtherFees?: null | number;
    expenseRecoveryAndOtherFeesPercentage?: null | number;
    netInterestEarnedOnCash?: null | number;
    netInterestEarnedOnCashPercentage?: null | number;
    hasCashBalances?: boolean;
    pysRefund?: null | number;
    pysRefundPercentage?: null | number;
    smaPortfolioFee?: null | number;
    smaPortfolioFeeExcPerf?: null | number;
    smaPortfolioFeePercentage?: null | number;
    smaPortfolioFeeExcPerfPercentage?: null | number;
  };
  feeDetails: FeeDetailDTO[]; //Client state => Convert from detail to array
  isLatest?: null;
  isRateCard?: null;
  investmentMenu?: string;
  outdatedAt?: null | Date;
  rateCardVersion?: null | number;
  isHidden: boolean;
  setupType: AnalysisFeePortfolioSetupType;
  multiMenuAccounts?: boolean;

  // internal use only
  recommendation?: ProductRecommendationDTO;
}

export interface FeeDTO {
  totalCostForDisplay: number;
  totalCostExcPerfForDisplay?: number;
  totalCostForDisplayPercentage: number;
  totalCostExcPerfForDisplayPercentage?: number;
  avgTotalPortfolio: number;
  isCurrentPortfolio?: boolean;

  //Optional
  idpsAdminForDisplay?: number;
  superAdminForDisplay?: number;
  totalAdminForDisplay?: number;
  totalAdminForDisplayPercentage?: number;
  allOtherFees?: number;
}

export interface FeeDetailDTO {
  name: string;
  dollar: number | null;
  dollarExcPerf?: number | null;
  percentage: number | null;
  percentageExcPerf?: number | null;
  tooltip?: FeeDetailTooltip;

  // Client state
  isSmaFees?: boolean;
  isPysRefund?: boolean;
}

export interface FeeDetailTooltip {
  title?: string | null;
  description?: string | null;
}

export type FeeCalculatorRenderConditions =
  | BooleanKeys<EditAnalysisFeeCalculatorReturnType>
  | { and: FeeCalculatorRenderConditions[] }
  | { or: FeeCalculatorRenderConditions[] };

export type FeeCalculatorDisableConditions = FeeCalculatorRenderConditions;

export type FeeCalculatorAutoCalculateConfig =
  | { sum: (AnalysisCalculateTotalTypeVariable | AnalysisVariable)[] }
  | {
      subtract: (AnalysisCalculateTotalTypeVariable | AnalysisVariable)[];
    };

export type FeeCalculatorValidStateConditions = {
  leftHandComparison: FeeCalculatorAutoCalculateConfig;
  operator: CompareOperator;
  rightHandComparison: FeeCalculatorAutoCalculateConfig | number;
  messageTemplate?: {
    plainText?: string;
    executor?: TemplateExecutor;
    variables?: AnalysisVariable[];
  };
};

export type FeeCalculatorRenderFieldProps = {
  // fieldPath: FieldPath<AnalysisFeeCalculatorDTO>;
  fieldConfig: FeeCalculatorRenderFieldConfig;
  disabled?: boolean;
};

export interface SectionConfigCalculationBadge {
  title?: string;
  autoCalculateConfig: FeeCalculatorAutoCalculateConfig;
}

export interface FeeCalculatorInvestmentSelectionConfig {
  disableConditions?: FeeCalculatorDisableConditions;
  defaultValueWhenDisabled?: string;
}

export interface FeeCalculatorRenderSectionConfig
  extends Omit<
    SetupStepRenderSectionConfig,
    "dependencyField" | "showFor" | "fields" | "name"
  > {
  id: FeeCalculatorSectionId;
  fields: FeeCalculatorRenderFieldConfig[];
  renderConditions?: FeeCalculatorRenderConditions;
  validateStateConditions?: FeeCalculatorValidStateConditions[];
  calculationBadge?: SectionConfigCalculationBadge;
  defaultVisibility?: boolean;
}

export interface FeeCalculatorRenderFieldConfig {
  name?: string;
  fieldType: FeeCalculatorRenderFieldType;
  renderConditions?: FeeCalculatorRenderConditions;
  singleVariableName?: AnalysisVariable;
  multipleVariableNames?: AnalysisVariable[];
  selectionVariableNames?: Partial<
    Record<AnalysisVariable, FeeCalculatorInvestmentSelectionConfig>
  >;
  component: (props: FeeCalculatorRenderFieldProps) => JSX.Element | null;
  autoCalculateConfig?: FeeCalculatorAutoCalculateConfig;
  linkedFieldVariableName?: AnalysisVariable[];
}

export interface FeeCalculatorCalculatedTotals {
  account: number;
  total: number;
  idps: number;
  super: number;
}
